import React from "react";
import { useParams } from "react-router-dom";
// Components
import DynamicHero from "components/DynamicHero";
import TextBlock from "components/TextBlock";
import { ImageContainer } from "styles/components/imageContainer";
import GhlFormEmbed from "components/GhlFormEmbed";
import SEO from "components/CBSEO";
import SchemaComponent from "components/CBSchema";
import CBDivider from "components/CBDivider";
import StartYourJourney from "pages/TherapyHub/components/startYourJourney";
import FAQContent from "components/CBFAQ";
import GetStartedToday from "pages/TherapyHub/components/GetStartedToday";
// Styled Components
import { StyledPageContainer } from "styles/components/PageContainer/index.styles";
import { TwoColumnSection } from "styles/components/twoColumnSection";
// Utils
import { constructSchemaData } from "utils/helpers/constructSchemaData";
// Hook
import useDynamicPageRenderer from "utils/hooks/useDynamicPageRenderer";
// Content Data
import onlineDepressionTestContent from "./data/pageContentData/onlineDepressionTestContent.data.json";
import onlineAnxietyTestContent from "./data/pageContentData/onlineAnxietyTestContent.data.json";
import onlinePanicDisorderTestContent from "./data/pageContentData/onlinePanicDisorderTestContent.data.json";
import onlinePtsdTestContent from "./data/pageContentData/onlinePtsdTestContent.data.json";
// SEO Data
import onlineDepressionTestSeoData from "./data/seoData/onlineDepressionTestSeo.data.json";
import onlineAnxietyTestSeoData from "./data/seoData/onlineAnxietyTestSeo.data.json";
import onlinePanicDisorderTestSeoData from "./data/seoData/onlinePanicDisorderTestSeo.data.json";
import onlinePtsdTestSeoData from "./data/seoData/onlinePtsdTestSeo.data.json";
// Schema Data
import onlineDepressionTestSchemaData from "./data/schemaData/onlineDepressionTestSchema.data.json";
import onlineAnxietyTestSchemaData from "./data/schemaData/onlineAnxietyTestSchema.data.json";
import onlinePanicDisorderTestSchemaData from "./data/schemaData/onlinePanicDisorderTestSchema.data.json";
import onlinePtsdTestSchemaData from "./data/schemaData/onlinePtsdTestSchema.data.json";
// Pages
import Page404 from "pages/404";

// Data Maps - mapping assessmentType to respective data
const assessmentContentMap = {
  "online-depression-test": onlineDepressionTestContent,
  "online-anxiety-test": onlineAnxietyTestContent,
  "online-panic-disorder-test": onlinePanicDisorderTestContent,
  "online-ptsd-test": onlinePtsdTestContent,
};

const assessmentSeoDataMap = {
  "online-depression-test": onlineDepressionTestSeoData,
  "online-anxiety-test": onlineAnxietyTestSeoData,
  "online-panic-disorder-test": onlinePanicDisorderTestSeoData,
  "online-ptsd-test": onlinePtsdTestSeoData,
};

const assessmentSchemaDataMap = {
  "online-depression-test": onlineDepressionTestSchemaData,
  "online-anxiety-test": onlineAnxietyTestSchemaData,
  "online-panic-disorder-test": onlinePanicDisorderTestSchemaData,
  "online-ptsd-test": onlinePtsdTestSchemaData,
};

// Components that expect props to be spread directly
const SPREAD_PROPS_COMPONENTS = ["DynamicHero", "TextBlock", "ThreeColumnSection", "Testimonials", "HowItWorks", "GhlFormEmbed"];

// Component Map - mapping component type strings to React components
const componentMap = {
  "DynamicHero": DynamicHero,
  "TextBlock": TextBlock,
  "TwoColumnSection": TwoColumnSection,
  "ImageContainer": ImageContainer,
  "GhlFormEmbed": GhlFormEmbed,
  "Divider": CBDivider,
  "StartYourJourney": StartYourJourney,
  "Faq": FAQContent,
  "GetStartedToday": GetStartedToday,
};

const OnlineAssessment = () => {
  const { assessmentType } = useParams();
  const content = assessmentContentMap[assessmentType] || [];
  const seoData = assessmentSeoDataMap[assessmentType] || {};
  const schemaDataRaw = assessmentSchemaDataMap[assessmentType] || {};
  const schemaData = schemaDataRaw.url ? constructSchemaData(schemaDataRaw.url, schemaDataRaw.breadcrumbs) : [];
  const renderedComponents = useDynamicPageRenderer(content, componentMap, SPREAD_PROPS_COMPONENTS);

  if (!content || content.length === 0) {
    return <Page404 />;
  }

  return (
    <StyledPageContainer id={`${assessmentType}-online-assessment-page-container`}>
      <SEO title={seoData.title} description={seoData.description} canonicalUrl={seoData.canonicalUrl} />
      <SchemaComponent schemas={schemaData} />

      {renderedComponents}
    </StyledPageContainer>
  );
};

export default OnlineAssessment;
